import React, { useEffect, useCallback } from 'react';
import { trackClick } from 'utility/analytics';
import history from 'historyContainer';
import Dotdotdot from 'react-dotdotdot';
import Spinner from 'components/Spinner/Spinner';
import { Waypoint } from 'react-waypoint';
import { Link } from 'react-router-dom';
import { useBranch } from 'baobab-react/hooks';
import { Avatar } from 'theme/Global.styles';
import { LG, SM, MD } from '@zendeskgarden/react-typography';
import moment from 'moment';
import Flex from 'styled-flex-component';
import { get, map, isString } from 'lodash';
import { getContentList, clearContent } from 'state/content/actions';
import { matchShape } from 'propTypesObjects';
import { PropTypes } from 'prop-types';
import {
  BlogPostsSection,
  BlogImg,
  BlogCard,
  BlogWrapper,
  BlogPostWrapper,
  BlogImgContainer,
  BlogPostHeader,
  BlogPostsSectionItems,
} from '../../Blog.styles';

function BlogPosts({ path, match, view }) {
  const { contentList } = useBranch({
    contentList: ['content', 'list'],
  });

  const generateFilter = useCallback(
    (_path) => {
      const { params } = match;
      const { tag } = params;
      let filters;
      if (tag) {
        filters = { tag_slug: tag };
      }
      if (!_path || _path === 'blog') {
        filters = {};
      } else {
        filters = { category_slug: _path };
      }
      return filters;
    },
    [match]
  );

  useEffect(() => {
    clearContent();
    const filters = generateFilter(path);
    getContentList({
      filters,
      pagination: { limit: 10 },
      sorting: { direction: 'desc' },
    });
  }, [generateFilter, path]);

  async function fetchMore() {
    const pagination = get(contentList, 'result.pagination', {});
    const filters = generateFilter(path);
    if (pagination.num_pages > pagination.page) {
      getContentList({
        filters,
        pagination: { limit: 10, page: pagination.page + 1 },
        sorting: { direction: 'desc' },
      });
    }
  }

  return (
    <BlogPostsSection>
      <BlogPostsSectionItems>
        {get(contentList, 'result.docs.length') ? (
          map(get(contentList, 'result.docs'), (content, i) => {
            if (path === '/video') {
              return (
                <BlogPostItem
                  view={view || 'blog'}
                  path={path}
                  content={content}
                  key={path}
                />
              );
            }
            return (
              <BlogPostItem
                view={view || 'blog'}
                path={path}
                content={content}
                key={i}
              />
            );
          })
        ) : (
          <Flex column alignCenter full>
            <LG center bold left tag="h2">
              There is Currently No Content for this Topic.
            </LG>
            <LG center bold left tag="h2">
              Check Back Soon
            </LG>
          </Flex>
        )}
      </BlogPostsSectionItems>
      {get(contentList, 'result.docs.length') && get(contentList, 'loading') ? (
        <Spinner />
      ) : null}
      {get(contentList, 'result.docs.length') > 8 ? (
        <Waypoint topOffset={2000} onEnter={() => fetchMore()} />
      ) : null}
    </BlogPostsSection>
  );
}

BlogPosts.propTypes = {
  path: PropTypes.string,
  match: matchShape,
  view: PropTypes.string,
};

export function BlogPostItem({ small, content, clamp = 3, path, view }) {
  const {
    id,
    preview,
    category_slug: categorySlug,
    slug,
    writer_name: writerName,
    published_date: publishedDate,
    preview_image: previewImage,
  } = content;

  let data =
    content?.preview && isString(preview) ? JSON.parse(preview) : preview;
  data = data || {};
  let link;
  if (path && path !== 'blog') {
    link = `${path}/${slug}/${id}`;
  }
  if (categorySlug) {
    link = `/${categorySlug}/${slug}/${id}`;
  } else {
    link = `/blog/${slug}/${id}`;
  }
  return (
    <BlogWrapper
      small={small}
      onClick={() => {
        trackClick(
          'viewContentSiderItem',
          { title: data.title, id },
          view || 'None'
        );
      }}
    >
      <BlogCard small={small} onClick={() => history.push(link)}>
        {!small ? (
          <BlogPostHeader>
            <Flex alignCenter>
              <Avatar
                position="center center"
                size="contain"
                noRadius
                src="/images/logo-blue-pipe.svg"
              />
              <div style={{ marginLeft: '13px' }}>
                <SM
                  tag="h6"
                  style={{
                    lineHeight: '18px',
                    marginBottom: '4px',
                    fontSize: '14px',
                  }}
                  bold
                >
                  {writerName || 'Pipesearch'}
                </SM>
                <SM
                  tag="h6"
                  style={{
                    lineHeight: '12px',
                    fontSize: '12px',
                    marginBottom: '4px',
                  }}
                  grey
                >
                  Published{' '}
                  {publishedDate ? moment(publishedDate).fromNow() : ''}
                </SM>
              </div>
            </Flex>
          </BlogPostHeader>
        ) : null}
        <BlogImgContainer small={small}>
          <BlogImg
            small={small}
            className="blog-image"
            src={previewImage || '/images/blog-hero-image.jpg'}
          />
        </BlogImgContainer>
        <BlogPostWrapper small={small}>
          <LG navy className="blog-title" bold left tag="h2">
            {data.title}
          </LG>
          <MD navy className="blog-description" tag="div">
            {clamp ? (
              <Dotdotdot clamp={clamp}>
                <p>{data.description}</p>
              </Dotdotdot>
            ) : (
              <p>{data.description}</p>
            )}
          </MD>
          <Link onClick={(e) => e.stopPropagation()} to={link}>
            <SM tag="span" link bold>
              Read More
            </SM>
          </Link>
        </BlogPostWrapper>
      </BlogCard>
    </BlogWrapper>
  );
}

BlogPostItem.propTypes = {
  small: PropTypes.bool,
  content: PropTypes.shape({
    id: PropTypes.number,
    preview: PropTypes.string,
    category_slug: PropTypes.string,
    slug: PropTypes.string,
    writer_name: PropTypes.string,
    published_date: PropTypes.string,
    preview_image: PropTypes.string,
  }),
  clamp: PropTypes.number,
  path: PropTypes.string,
  view: PropTypes.string,
};

export default BlogPosts;
